import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useAccountStatements from 'features/myVaro/hooks/statementsAndForms/useAccountStatements';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';

const AccountMonthSelectionScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'statementsAndForms',
  });
  const { monthSelectionScreenTitle, setMonthSelectionScreenTitle } =
    useContext(MyVaroContext);

  const {
    id,
    year,
    category = '',
    account_type = '',
  } = useLocalSearchParams<{
    id: string;
    year: string;
    category: string;
    account_type: string;
  }>();

  const { monthlyStatementsData, yearlyStatementsData, loading, errorModel } =
    useAccountStatements({
      accountId: id,
      year: year,
    });

  const flow = useVaroUserFlow();

  const onMonthPress = (month: string) => {
    router.push({
      pathname: `/${flow}/statements-and-forms/account-statements/[id]/[year]/[month]`,
      params: {
        id: id,
        year: year,
        month: month.toLowerCase(),
        category,
        account_type,
      },
    });
  };

  //Handles Screen title if we hit the url directly or get redirected from another screen
  useEffect(() => {
    yearlyStatementsData?.forEach((statement) => {
      if (statement?.year?.toString() === year) {
        setMonthSelectionScreenTitle?.(statement.title);
      }
    });
  }, [yearlyStatementsData, year, setMonthSelectionScreenTitle]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: monthSelectionScreenTitle,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {monthlyStatementsData?.length ? (
        <FlatList
          data={monthlyStatementsData}
          renderItem={({ item }) => (
            <ListItem
              key={item?.title}
              primaryCopy={item?.title}
              onPress={() => {
                setMonthSelectionScreenTitle?.(item?.title);
                onMonthPress(item?.title ?? '');
              }}
              analyticsId="month-pressed"
              customAttributes={{
                account_type,
                category,
                cta: item?.title,
              }}
            />
          )}
          ItemSeparatorComponent={() => (
            <View style={styles.hairLineDivider}>
              <HairLineDivider />
            </View>
          )}
          keyExtractor={(item) => item?.title}
          removeClippedSubviews={false}
        />
      ) : (
        <View style={styles.emptyStateContainer}>
          {!loading ? (
            <EmptyStateScreen
              headlineText={translatedText('accountStatements.title')}
              illustrationKey="BANK_A"
              bodyText={translatedText('accountStatements.emptyStateBodyText')}
            />
          ) : null}
        </View>
      )}
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexGrow: 1,
  },
  emptyStateContainer: { flexGrow: 1 },
  hairLineDivider: { marginHorizontal: theme.spacing.m },
});

export default AccountMonthSelectionScreen;
